<template>
  <WDrawer
    v-model="isOpen"
    class="claude-drawer-frame"
    section-tag="ticket-timeline-drawer"
    :head-line="headLine"
    max-width="720px"
  >
    <div
      ref="body"
      class="ticket-timeline"
      tabindex="-1"
      :aria-label="headLine"
      data-testid="ticket-timeline-body"
    >
      <p v-if="subHeadLine" class="ticket-timeline__summary" data-testid="ticket-timeline-summary">
        {{ subHeadLine }}
      </p>
      <WLoader v-if="isLoading && !timeline" color="dark" section-tag="ticket-timeline-loading" />
      <EmptyState
        v-else-if="!timeline && error"
        headline="The timeline could not be read"
        :body="errorText"
        :icon="mdiAlertCircleOutline"
        section-tag="ticket-timeline-error"
      />
      <template v-else-if="timeline">
        <ul
          v-if="timeline.notes.length"
          class="ticket-timeline__notes"
          data-testid="ticket-timeline-notes"
        >
          <li v-for="note in timeline.notes" :key="note">{{ note }}</li>
        </ul>
        <EmptyState
          v-if="!timeline.events.length"
          headline="Nothing yet"
          :body="emptyBody"
          :icon="mdiTimelineClockOutline"
          section-tag="ticket-timeline-empty"
        />
        <section v-for="day in days" :key="day.day" class="ticket-timeline__day">
          <h3 class="ticket-timeline__date">{{ formatDay(day.day) }}</h3>
          <TicketTimelineEvent
            v-for="(event, index) in day.events"
            :key="`${day.day}-${index}`"
            :event="event"
          />
        </section>
      </template>
    </div>
  </WDrawer>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'

import { useTicketTimelineStore } from '@/stores/ticket-timeline.store'

import { myWorkErrorText } from '@/services/my-work'
import { timelineDays, useTicketTimelineService } from '@/services/ticket-timeline'

import WDrawer from '@wasabi/content-displays/WDrawer.vue'
import WLoader from '@wasabi/data-visualization/WLoader.vue'
import EmptyState from '@wasabi/patterns/EmptyState.vue'

import TicketTimelineEvent from './TicketTimelineEvent.vue'

import { mdiAlertCircleOutline, mdiTimelineClockOutline } from '@mdi/js'

import { formatDate } from '@/utils/dateUtils'

const store = useTicketTimelineStore()

const { useTicketTimeline } = useTicketTimelineService()

const { data, isLoading, error } = useTicketTimeline(() => store.openKey)

const isOpen = computed({
  get: () => store.isOpen,
  set: open => {
    if (!open) store.close()
  }
})

/** The answer for the key that is open now, never a previous ticket's while the new one loads. */
const timeline = computed(() => (data.value?.key === store.openKey ? data.value : null))

const headLine = computed(() => (store.openKey ? `Timeline · ${store.openKey}` : 'Timeline'))

/** The ticket's summary goes in the body, not WDrawer's sub-headline: a long one wraps the header row
 *  and pushes the close button under it. */
const subHeadLine = computed(() => timeline.value?.summary ?? '')

const days = computed(() => timelineDays(timeline.value?.events ?? []))

const errorText = computed(() => myWorkErrorText(error.value, 'The fleet API did not answer.'))

const emptyBody = computed(
  () =>
    `No fleet run in the last ${timeline.value?.runWindowDays ?? 90} days, PR or comment from Claude on this ticket.`
)

const body = ref<HTMLElement | null>(null)
let returnFocus: HTMLElement | null = null

/** Keyboard and screen-reader users land in the drawer when it opens, and back on what opened it when
 *  it closes. The drawer slides in, so focus waits a frame past the next render. */
watch(
  () => store.isOpen,
  async open => {
    if (open) {
      returnFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null
      await nextTick()
      requestAnimationFrame(() => {
        // A drawer closed before this frame has already handed focus back; its hidden body must not take it.
        if (store.isOpen) body.value?.focus({ preventScroll: true })
      })
    } else if (returnFocus?.isConnected) {
      returnFocus.focus({ preventScroll: true })
      returnFocus = null
    }
  }
)

function formatDay(day: string): string {
  return day === 'Undated' ? day : formatDate(`${day}T12:00:00Z`)
}
</script>

<style scoped>
.ticket-timeline {
  outline: none;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.ticket-timeline__summary {
  margin: 0 0 12px;
  color: rgb(var(--v-theme-neutral600));
}

.ticket-timeline__notes {
  margin: 0;
  padding-left: 20px;
}

.ticket-timeline__day {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ticket-timeline__date {
  margin: 0;
  font-size: 13px;
  color: rgb(var(--v-theme-neutral600));
}
</style>
